import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Breaker,
  Button,
  Flexbox,
  MultiDropdown,
  ToggleButton,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { CloseCircle } from "../../Theme/Icons";
import { globalTheme } from "../../Theme/Theme";
import { PopupBody, PopupWrapper } from "./CommonPopup";
import { coworkData, getEquipmentList } from "../../../redux/cowork/coworkSlice";

/**
 * Makers Lab booking confirmation. Mirrors the legacy ConfirmLabBookingPopup
 * (equipment selection + "need instructor") but in the new dark popup theme.
 *
 * Presentational: it only collects equipment + instructor preference and hands
 * them back via onConfirm — the parent owns the actual booking dispatch.
 *
 * @param {boolean}  showActive   - controls visibility
 * @param {Object}   room         - the lab room being booked (for title + fallback equipment)
 * @param {boolean}  isSubmitting - a booking request is in flight
 * @param {Function} onCancel     - called to close the popup
 * @param {Function} onConfirm    - called with { equipments: id[], needInstructor: boolean }
 */
const ConfirmLabBookingPopup = ({
  showActive,
  room,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const dispatch = useDispatch();
  const { equipmentList } = useSelector(coworkData);

  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [needInstructor, setNeedInstructor] = useState(false);

  // Load the equipment catalogue the first time the popup opens.
  useEffect(() => {
    if (showActive && !(equipmentList && equipmentList.length)) {
      dispatch(getEquipmentList());
    }
  }, [showActive, equipmentList, dispatch]);

  // Reset selections every time the popup is closed so a new booking starts clean.
  useEffect(() => {
    if (!showActive) {
      setSelectedEquipments([]);
      setNeedInstructor(false);
    }
  }, [showActive]);

  // MultiDropdown shape: { cat: value, key: label }. Prefer the global
  // catalogue; fall back to whatever equipment the room itself lists.
  const equipmentOptions = useMemo(() => {
    const source =
      equipmentList && equipmentList.length ? equipmentList : room?.equipments || [];
    return source.map((e) => ({ cat: e?.id ?? e, key: e?.name ?? e }));
  }, [equipmentList, room]);

  const handleConfirm = () => {
    if (!selectedEquipments || selectedEquipments.length === 0) {
      toast.error("Please select at least one equipment", {
        autoClose: 3000,
      });
      return;
    }
    onConfirm({
      equipments: selectedEquipments.map((e) => e.cat),
      needInstructor,
    });
  };

  return (
    <PopupWrapper className={`${showActive ? "active" : ""}`}>
      <PopupBody maxWidth={520}>
        <Flexbox spaceBW alignCenter>
          <Text font18 inter medium white maxWidth="max-content">
            Confirm Lab Booking
          </Text>
          <CloseCircle className="pointer" onClick={() => onCancel(false)} />
        </Flexbox>
        <Breaker marginTop={12} marginBottom={20} borderColor={globalTheme.colors.grey2} />

        {room && (
          <Flexbox column gap={4} className="mb20" maxWidth="max-content">
            <Text font16 inter medium white>
              {room.room_name}
            </Text>
            {room.tower != null && (
              <Text font14 inter regular grey2>
                Tower {room.tower}
              </Text>
            )}
          </Flexbox>
        )}

        <Text font14 inter medium white className="mb12">
          Select Equipment <span style={{ color: globalTheme.colors.red }}>*</span>
        </Text>
        <MultiDropdown
          noLabel
          placeholder="Choose equipment for your session"
          options={equipmentOptions}
          selectedValues={selectedEquipments}
          onChange={(list) => setSelectedEquipments(list || [])}
        />

        <Flexbox spaceBW alignCenter className="mt20">
          <Flexbox column gap={4} maxWidth="max-content">
            <Text font16 inter medium white>
              Need an instructor?
            </Text>
            <Text font12 inter regular grey2>
              Request a lab instructor for your session
            </Text>
          </Flexbox>
          <ToggleButton
            round
            checked={needInstructor}
            width={"40"}
            height={"22"}
            toggleSize="18"
            onChange={(e) => setNeedInstructor(e.target.checked)}
          />
        </Flexbox>

        <Flexbox alignCenter gap={12} justiCenter className="mt30">
          <Button
            buttonMd
            greyFilled
            fullBtn
            name="Cancel"
            disabled={isSubmitting}
            onClick={() => onCancel(false)}
          />
          <Button
            buttonMd
            primaryFilled
            fullBtn
            name={isSubmitting ? "Booking..." : "Confirm Booking"}
            isWaiting={isSubmitting}
            disabled={isSubmitting}
            onClick={handleConfirm}
          />
        </Flexbox>
      </PopupBody>
    </PopupWrapper>
  );
};

export default ConfirmLabBookingPopup;
